import { useState, useRef, useEffect } from 'react';
import TextInput from '~/components/TextInput';
import InputContainer from '~/components/InputContainer';
import { ChevronRightIcon, PlusIcon } from '@heroicons/react/24/outline';
import SortableList from '~/components/SortableList';
import IconSort from '~/components/icons/IconSort';
import type { QuestionSettings } from '~/lib/Questions/Questions.types';
import { formatCurrency } from '~/lib/CurrencyHelpers';
import MoneyInput from '~/components/MoneyInput';
import SidePanel from '~/components/SidePanel';
import {
    addLineItem as addItem,
    removeLineItem as removeItem,
    updateLineItem as updateItem,
    setLineItemFields,
    calculateLineItemTotal,
    calculateGrandTotal,
} from '~/lib/InvoiceLineItemHelpers';
import { useSavedLineItemsQuery } from '~/lib/Queries';
import useNumericDraft from '~/lib/useNumericDraft';

interface LineItem {
    name: string;
    description?: string;
    quantity: number;
    unitPrice: number;
}

interface EditInvoiceLineItemsFormProps {
    settings: QuestionSettings;
    setSettings: (settings: QuestionSettings) => void;
}

const LineItemNameCombobox = ({ value, onChange, onSelectSavedItem, placeholder, inputClassName, autoFocus }: {
    value: string;
    onChange: (value: string) => void;
    onSelectSavedItem: (item: any) => void;
    placeholder?: string;
    inputClassName?: string;
    autoFocus?: boolean;
}) => {
    const { data } = useSavedLineItemsQuery();
    const savedLineItems = data?.savedLineItems || [];
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value || '');
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filtered = savedLineItems.filter((item: any) =>
        item.name.toLowerCase().includes(inputValue.toLowerCase())
    );

    const handleInputChange = (val: string) => {
        setInputValue(val);
        onChange(val);
        setIsOpen(true);
    };

    const handleSelect = (item: any) => {
        setInputValue(item.name);
        onSelectSavedItem(item);
        setIsOpen(false);
    };

    const showDropdown = isOpen && savedLineItems.length > 0 && filtered.length > 0;

    return (
        <div ref={containerRef} className="relative">
            <InputContainer label="Item Name">
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onFocus={() => setIsOpen(true)}
                    placeholder={placeholder}
                    className={inputClassName}
                    autoFocus={autoFocus}
                />
            </InputContainer>
            {showDropdown && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filtered.map((item: any) => (
                        <button
                            key={item.id}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSelect(item)}
                        >
                            <div className="font-medium text-sm">{item.name}</div>
                            {item.description && <div className="text-sm text-gray-500">{item.description}</div>}
                            <div className="text-xs text-gray-400">
                                {item.quantity} × {formatCurrency(item.price)}
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const UnitPriceInput = ({ value, onChange }: {
    value: number | string | undefined;
    onChange: (value: number | '') => void;
}) => {
    const [text, handleChange] = useNumericDraft(value, onChange);

    return (
        <MoneyInput
            label="Unit Price"
            value={text}
            onChange={handleChange}
            className="mt-6"
            inputClassName="placeholder:text-gray-400"
        />
    );
};

const LineItemFields = ({ item, index, settings, setSettings }: {
    item: LineItem;
    index: number;
    settings: QuestionSettings;
    setSettings: (settings: QuestionSettings) => void;
}) => (
    <>
        <LineItemNameCombobox
            value={item.name}
            autoFocus={!item.name}
            onChange={(value) => updateItem(settings, setSettings, index, 'name', value)}
            onSelectSavedItem={(savedItem) => setLineItemFields(settings, setSettings, index, {
                name: savedItem.name,
                description: savedItem.description || '',
                quantity: savedItem.quantity,
                unitPrice: savedItem.price,
            })}
            placeholder="Search saved items or enter a name"
            inputClassName="placeholder:text-gray-400"
        />

        <TextInput
            label="Description (optional)"
            value={item.description || ''}
            onChange={(value: string) => updateItem(settings, setSettings, index, 'description', value)}
            placeholder="Additional details about this item"
            className="mt-6"
            inputClassName="placeholder:text-gray-400"
        />

        <TextInput
            label="Quantity"
            type="number"
            value={item.quantity?.toString() || '1'}
            onChange={(value: string) => updateItem(settings, setSettings, index, 'quantity', parseFloat(value) || 0)}
            min="0"
            step="1"
            className="mt-6"
            inputClassName="placeholder:text-gray-400"
        />

        <UnitPriceInput
            value={item.unitPrice}
            onChange={(value) => updateItem(settings, setSettings, index, 'unitPrice', value)}
        />
    </>
);

const LineItemListItem = ({ item, onClick }: { item: LineItem; onClick: () => void }) => (
    <div
        onClick={onClick}
        className="cursor-pointer group py-3 pl-4 pr-4 flex justify-between items-center"
    >
        <div className="flex items-start">
            <IconSort className="cursor-grab text-gray-500 inline-block -ml-8 opacity-0 group-hover:opacity-100" />
            <div>
                <div>{item.name || 'Unnamed Item'}</div>
                <div className="text-gray-500 text-sm">
                    {item.quantity > 0 && (
                        <span className="mr-4">
                            {item.quantity} × {formatCurrency(item.unitPrice || 0)}
                        </span>
                    )}
                    <span>{formatCurrency(calculateLineItemTotal(item))}</span>
                </div>
            </div>
        </div>
        <ChevronRightIcon className="w-5 h-5 text-gray-300 group-hover:text-gray-500" />
    </div>
);

const EditLineItemPanel = ({ item, index, settings, setSettings, isOpen, onClose, onDelete }: {
    item: LineItem;
    index: number;
    settings: QuestionSettings;
    setSettings: (settings: QuestionSettings) => void;
    isOpen: boolean;
    onClose: () => void;
    onDelete: () => void;
}) => {
    const [showError, setShowError] = useState(false);

    const handleSave = () => {
        if (!item.name?.trim()) {
            setShowError(true);
            return;
        }
        setShowError(false);
        onClose();
    };

    // Clear error when panel closes or name changes
    useEffect(() => {
        if (!isOpen) setShowError(false);
    }, [isOpen]);

    useEffect(() => {
        if (item.name?.trim()) setShowError(false);
    }, [item.name]);

    return (
        <SidePanel title="Edit Line Item" isOpen={isOpen} onClose={onClose}>
            <LineItemFields
                item={item}
                index={index}
                settings={settings}
                setSettings={setSettings}
            />

            <div className="mt-6">
                <span className="font-bold">Total:</span> {formatCurrency(calculateLineItemTotal(item))}
            </div>

            {showError && (
                <div className="mt-4 text-red-600 text-sm">Item name is required.</div>
            )}

            <div className="mt-4 flex items-center justify-between">
                <button
                    className="btn-save"
                    type="button"
                    onClick={handleSave}
                >
                    Save
                </button>
                <button
                    className="text-red-700"
                    type="button"
                    onClick={() => {
                        onDelete();
                        onClose();
                    }}
                >
                    Delete
                </button>
            </div>
        </SidePanel>
    );
};

const EditInvoiceLineItemsForm = ({ settings, setSettings }: EditInvoiceLineItemsFormProps) => {
    const lineItems: LineItem[] = (settings?.lineItems as LineItem[]) || [];
    const [selectedItem, setSelectedItem] = useState<number | null>(null);

    // Track whether we started with zero items on mount — if so, show inline form
    const startedEmpty = useRef(lineItems.length === 0);

    useEffect(() => {
        if (startedEmpty.current && lineItems.length === 0) {
            addItem(settings, setSettings);
        }
    }, []);

    // Show inline form if we started empty and still have at most 1 item
    const inlineItem = startedEmpty.current && lineItems.length <= 1;

    return (
        <div>
            <div className="flex items-center justify-between">
                <div className="input-label">Line Items</div>
            </div>

            {inlineItem ? (
                lineItems.length === 1 && (
                    <div className="rounded-lg p-4 mb-2 bg-white">
                        <LineItemFields
                            item={lineItems[0]}
                            index={0}
                            settings={settings}
                            setSettings={setSettings}
                        />
                    </div>
                )
            ) : (
                <>
                    <div className="panel pl-4">
                        <SortableList
                            items={lineItems.map((item, idx) => ({ ...item, id: idx }))}
                            onReorder={newItems => {
                                const reordered = newItems.map(({ id, ...rest }) => rest);
                                setSettings({ ...settings, lineItems: reordered });
                            }}
                            containerClassName="divide-y"
                            renderItem={({ item }) => (
                                <LineItemListItem
                                    item={item}
                                    onClick={() => setSelectedItem(item.id)}
                                />
                            )}
                        />
                    </div>

                    {lineItems.length > 0 && (
                        <div className="flex justify-end items-center gap-2 mt-4 p-4 font-semibold">
                            <span className="text-gray-900">Total:</span>
                            <span className="text-gray-900">
                                {formatCurrency(calculateGrandTotal(lineItems))}
                            </span>
                        </div>
                    )}
                </>
            )}

            <button
                type="button"
                onClick={() => {
                    addItem(settings, setSettings);
                    // Open the panel for the newly added item
                    setSelectedItem(lineItems.length);
                }}
                className="btn-light-pill text-sm flex items-center gap-1 mt-2"
            >
                <PlusIcon className="w-4 h-4" />
                {lineItems.length === 1 ? 'Add Another Line Item' : 'Add New Line Item'}
            </button>

            <EditLineItemPanel
                item={selectedItem !== null ? lineItems[selectedItem] : { name: '', quantity: 1, unitPrice: 0 }}
                index={selectedItem ?? 0}
                settings={settings}
                setSettings={setSettings}
                isOpen={selectedItem !== null}
                onClose={() => setSelectedItem(null)}
                onDelete={() => {
                    if (selectedItem !== null) {
                        removeItem(settings, setSettings, selectedItem);
                    }
                }}
            />
        </div>
    );
};

export default EditInvoiceLineItemsForm;

